import React, { useState } from "react";
import {
  Input, Modal, Button, Tooltip, Radio,
} from "antd";
import Highlighter from "react-highlight-words";
import {
  SPEC_FILTER_VALUES,
  getProductIdentifierFieldEntries,
  isCustomSpecProduct,
  getTestSpecEntityLabels,
} from "../utils/utils";
import addedStatusIcon from "../../../../assets/images/product/specmanager/checkbox-circle-line.svg";
import pendingStatusIcon from "../../../../assets/images/product/specmanager/circle-line.svg";
import filterSeparatorIcon from "../../../../assets/images/product/specmanager/Seperator.svg";
import shieldCheckIcon from "../../../../assets/images/product/specmanager/shield-check-line.svg";
import informationIcon from "../../../../assets/images/product/specmanager/information-line.svg";

const FILTER_OPTIONS = [
  {
    value: SPEC_FILTER_VALUES.ALL, label: "All", typeClass: "all", icon: null,
  },
  {
    value: SPEC_FILTER_VALUES.ADDED, label: "Spec Added", typeClass: "added", icon: addedStatusIcon,
  },
  {
    value: SPEC_FILTER_VALUES.PENDING, label: "Spec Incomplete", typeClass: "pending", icon: pendingStatusIcon,
  },
];

/** Status chip for list rows (variant=list) or overview/preview headers (variant=overview). */
export function SpecStatusChip({ status, variant = "list" }) {
  const isAdded = status === "added";
  const prefix = variant === "overview" ? "TestSpecOverview" : "SpecList";

  return (
    <span className={`${prefix}_StatusChip${isAdded ? ` ${prefix}_StatusChip--added` : ` ${prefix}_StatusChip--pending`}`}>
      <img
        src={isAdded ? addedStatusIcon : pendingStatusIcon}
        alt=""
        className={`${prefix}_StatusChipIcon`}
        aria-hidden
      />
      <span>{isAdded ? "Spec Added" : "Incomplete"}</span>
    </span>
  );
}

export function CoverageSummarySection({ products, specType }) {
  const labels = getTestSpecEntityLabels(specType);
  const customCount = products.filter(isCustomSpecProduct).length;
  const totalCount = products.length;
  const standardCount = Math.max(totalCount - customCount, 0);

  return (
    <div className="TestSpecOverview_CoverageSection">
      <div className="TestSpecOverview_SectionTitleBar">
        <div className="TestSpecOverview_SectionTitle">Coverage Summary</div>
      </div>
      <div className="TestSpecOverview_CoverageRows">
        <div className="TestSpecOverview_CoverageRow">
          <div className="TestSpecOverview_RowLabel">{labels.totalLabel}</div>
          <div className="TestSpecOverview_Badge TestSpecOverview_Badge--total">
            {totalCount}
          </div>
        </div>
        <div className="TestSpecOverview_CoverageRow">
          <div className="TestSpecOverview_RowLabel">{labels.standardLabel}</div>
          <Tooltip title={labels.standardTooltip}>
            <div className="TestSpecOverview_Badge TestSpecOverview_Badge--standard">
              <img src={shieldCheckIcon} alt="" className="TestSpecOverview_BadgeIcon" aria-hidden />
              {standardCount}
            </div>
          </Tooltip>
        </div>
        {labels.showCustomCoverage && (
          <div className="TestSpecOverview_CoverageRow">
            <div className="TestSpecOverview_RowLabel">{labels.customLabel}</div>
            <Tooltip title={labels.customTooltip}>
              <div className="TestSpecOverview_Badge TestSpecOverview_Badge--custom">
                <img src={informationIcon} alt="" className="TestSpecOverview_BadgeIcon" aria-hidden />
                {customCount}
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

export function DiscardChangesModal({ open, onCancel, onConfirm }) {
  return (
    <Modal
      open={open}
      title="Discard Changes?"
      onCancel={onCancel}
      wrapClassName="SpecManager_SubModal_Wrap"
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="discard" type="primary" danger onClick={onConfirm}>Discard</Button>,
      ]}
      centered
      destroyOnClose
    >
      <p>You have unsaved changes. Are you sure you want to discard them?</p>
    </Modal>
  );
}

export function SpecFilterButtons({ activeFilter, onFilterChange }) {
  return (
    <div className="SpecManager_Filters">
      {FILTER_OPTIONS.map((filterOption, index) => {
        const isActive = activeFilter === filterOption.value
          || (filterOption.value === SPEC_FILTER_VALUES.ALL && activeFilter === "");
        const buttonClassName = [
          "SpecManager_FilterButton",
          `SpecManager_FilterButton--${filterOption.typeClass}`,
          isActive ? "SpecManager_FilterButton--active" : "",
        ].filter(Boolean).join(" ");

        return (
          <React.Fragment key={filterOption.value || "all"}>
            {index === 1 && (
              <img src={filterSeparatorIcon} alt="" className="SpecManager_FilterSeparator" aria-hidden />
            )}
            <button
              type="button"
              className={buttonClassName}
              onClick={() => onFilterChange(
                activeFilter === filterOption.value && filterOption.value !== SPEC_FILTER_VALUES.ALL
                  ? SPEC_FILTER_VALUES.ALL
                  : filterOption.value,
              )}
            >
              {filterOption.icon && (
                <img src={filterOption.icon} alt="" className="SpecManager_FilterButtonIcon" aria-hidden />
              )}
              <span>{filterOption.label}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function SpecListSearchInput({
  placeholder = "Search...",
  value,
  onChange,
  onSearch,
}) {
  return (
    <Input.Search
      className="SpecManager_SearchInput"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onSearch={onSearch}
      allowClear
    />
  );
}

export function ProductIdentifierDisplay({
  productRow,
  searchValue,
  searchField,
  identifierFields,
}) {
  const entries = getProductIdentifierFieldEntries(productRow, identifierFields);

  return (
    <span className="SpecList_IdentifierFields">
      {entries.map((entry, index) => (
        <React.Fragment key={entry.jsonField}>
          <span className="SpecList_IdentifierLabel">
            {entry.titleField}
            :
          </span>
          <Highlighter
            className="SpecList_IdentifierValue"
            highlightClassName="SpecList_IdentifierHighlight"
            searchWords={searchField === entry.jsonField ? [searchValue] : []}
            autoEscape
            textToHighlight={entry.value}
          />
          {index < entries.length - 1 && (
            <span className="SpecList_IdentifierDelimiter" aria-hidden />
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

/** Product list search with Item / Description field picker (ESV parity). */
export function ProductSpecSearchInput({
  placeholder = "Search products...",
  value,
  searchField,
  identifierFields,
  onChange,
  onSearch,
  onSearchFieldChange,
}) {
  const [showFieldPicker, setShowFieldPicker] = useState(false);

  return (
    <div
      className="SpecManager_ProductSearch"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setShowFieldPicker(false);
        }
      }}
    >
      <Input.Search
        className="SpecManager_SearchInput"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onSearch={onSearch}
        onFocus={() => setShowFieldPicker(true)}
        allowClear
      />
      {showFieldPicker && identifierFields.length > 1 && (
        <div className="SpecManager_SearchFieldPicker" role="presentation">
          <Radio.Group
            value={searchField}
            onChange={(event) => onSearchFieldChange(event.target.value)}
            options={identifierFields.map((field) => ({
              label: field.title_field,
              value: field.json_field,
            }))}
          />
        </div>
      )}
    </div>
  );
}
